import { Injectable } from "@angular/core";
import { ApiService } from "../shared/api.service";
import { Observable } from "rxjs";
import { User } from "@models/user/user.model";
import { Client, ProjectTemplate } from "@models/template/template.model";
import {
  AddElevenLabsVoiceRequest,
  AzureVoice,
  ElevenLabsPublicVoiceFilters,
  ElevenLabsVoiceSearchResult,
  ResponseFromApi,
} from "@models/textToSpeech/textToSpeech.model";
import { Tenant } from "@models/tenant/tenant.model";

@Injectable({
  providedIn: "root",
})
export class ManagementService {
  constructor(private api: ApiService) {}

  public getUsers(): Observable<User[]> {
    return this.api.get("user/getAll");
  }

  public getUsersToNotify(): Observable<User[]> {
    return this.api.get("user/getNotificationList");
  }

  public getUserById(userId: number): Observable<User> {
    return this.api.get("user/getById?userId=" + userId);
  }

  public addNewUser(user: User) {
    user.host = document.location.origin;
    return this.api.post("user/add", user);
  }

  public updateUser(user: User): Observable<User> {
    return this.api.post("user/update", user);
  }

  public deleteUser(userId: number): Observable<any> {
    return this.api.get("user/deleteById?userId=" + userId);
  }

  public getCurrentUserType(): Observable<boolean> {
    return this.api.get("user/isLoggedInUserAdmin");
  }

  // template section
  public getAllTemplates(): Observable<ProjectTemplate[]> {
    return this.api.get("template/getAll");
  }

  public getTemplateById(id: number): Observable<ProjectTemplate> {
    return this.api.get("template/getById/" + id);
  }

  public addTemplate(template: ProjectTemplate): Observable<ResponseFromApi> {
    return this.api.post("template/create", template);
  }

  public deleteTemplate(id: number): Observable<void> {
    return this.api.delete("template/delete/" + id);
  }

  public updateTemplate(
    template: ProjectTemplate
  ): Observable<ResponseFromApi> {
    return this.api.post("template/update", template);
  }

  public getClients(): Observable<Client[]> {
    return this.api.get("template/getClients");
  }

  // Tenants section

  public getTenants(): Observable<Tenant[]> {
    return this.api.get("tenant/getAll");
  }

  public getTenantsByUser(id: number): Observable<Tenant[]> {
    return this.api.get(`tenant/getByUserId/${id}`);
  }

  public getTenantById(id: string): Observable<Tenant> {
    return this.api.get(`tenant/getById/${id}`);
  }

  public createTenant(tenant: Tenant): Observable<ResponseFromApi> {
    return this.api.post(`tenant/create`, tenant);
  }

  public updateTenant(tenant: Tenant): Observable<ResponseFromApi> {
    return this.api.post(`tenant/update`, tenant);
  }

  public deleteTenant(id: string): Observable<ResponseFromApi> {
    return this.api.get(`tenant/delete/${id}`);
  }

  // Session section
  public updateSessionTenantId(tenantId: string) {
    return this.api.post(`user/updateSessionTenantId`, tenantId);
  }

  // ElevenLabs voice management section
  public getElevenLabsDbVoices(): Observable<AzureVoice[]> {
    return this.api.get("voice/elevenlabs/db");
  }

  public updateElevenLabsVoiceStatus(
    id: number,
    isActive: boolean
  ): Observable<ResponseFromApi> {
    return this.api.patch(`voice/${id}/status`, { isActive });
  }

  public deleteElevenLabsVoice(id: number): Observable<ResponseFromApi> {
    return this.api.delete(`voice/${id}`);
  }

  public getElevenLabsPersonalVoices(
    search?: string,
    nextPageToken?: string
  ): Observable<ElevenLabsVoiceSearchResult> {
    let url = "voice/elevenlabs/personal";
    const params: string[] = [];
    if (search) {
      params.push(`search=${encodeURIComponent(search)}`);
    }
    if (nextPageToken) {
      params.push(`nextPageToken=${encodeURIComponent(nextPageToken)}`);
    }
    if (params.length) {
      url += "?" + params.join("&");
    }
    return this.api.get(url);
  }

  public getElevenLabsPublicVoices(
    filters: ElevenLabsPublicVoiceFilters = {}
  ): Observable<ElevenLabsVoiceSearchResult> {
    let url = "voice/elevenlabs/public";
    const params: string[] = [];
    Object.keys(filters).forEach((key) => {
      const value = (filters as any)[key];
      if (value !== undefined && value !== null && value !== "") {
        params.push(`${key}=${encodeURIComponent(String(value))}`);
      }
    });
    if (params.length) {
      url += "?" + params.join("&");
    }
    return this.api.get(url);
  }

  public addElevenLabsVoice(
    request: AddElevenLabsVoiceRequest
  ): Observable<ResponseFromApi> {
    return this.api.post("voice/elevenlabs/add", request);
  }

  public previewVoiceText(
    voiceId: string,
    text: string
  ): Observable<ResponseFromApi> {
    return this.api.post("voice/preview-text", { voiceId, text });
  }
}
